import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import { ActiveCartFacade } from '@spartacus/cart/base/root'
import { AuthService, Product } from '@spartacus/core'
import { CurrentProductService, LaunchDialogService } from '@spartacus/storefront'
import { Observable, Subscription } from 'rxjs'
import { take } from 'rxjs/operators'
import { LAUNCH_CALLER } from '../../../models/enum/custom-dialog.enum'
import { ICustomProduct, ProductUnit, UomMapping } from '../../../models/interface/product.interface'
import { UomSelectOption } from '../../../models/uom.model'
import { CustomCartService } from '../../../services/custom-cart.service'
import { ProductDetailsService } from '../../../services/product.service'
import { SharedService } from '../../../services/shared/shared.service'
import { UserPermissionService } from '../../../services/user-permission/user-permission.service'
import { CustomProductReplacementService } from '../../product/custom-product-replacement/custom-product-replacement.service'

@Component({
  selector: 'app-custom-add-to-cart',
  templateUrl: './custom-add-to-cart.component.html',
  styleUrls: ['./custom-add-to-cart.component.scss'],
  standalone: false
})
export class CustomAddToCartComponent implements OnInit, OnDestroy {
  @Input() productCode: string
  @Input() showQuantity = true
  @Input() product: ICustomProduct
  @Input() public buttonClass = ''
  public uomSelectOptions: UomSelectOption[] = []
  public uomSelected: UomSelectOption = new UomSelectOption()
  public defaultUomName: string
  public price: string
  maxQuantity: number
  // modalRef: ModalRef;
  // modalRef: any;
  isLoggedIn$: Observable<boolean>

  hasStock = false
  quantity = 1
  protected numberOfEntriesBeforeAdd = 0

  subscription: Subscription

  addToCartForm = new FormGroup({
    quantity: new FormControl(1)
  })
  constructor(
    protected launchDialogService: LaunchDialogService,
    protected currentProductService: CurrentProductService,
    private cd: ChangeDetectorRef,
    protected activeCartService: ActiveCartFacade,
    private customCartService: CustomCartService,
    private authService: AuthService,
    public userPermissionService: UserPermissionService,
    public productDetailsService: ProductDetailsService,
    public sharedService: SharedService,
    protected vcr: ViewContainerRef,
    protected customProductReplacementService: CustomProductReplacementService
    // public customLaunchDialogService : customLaunchDialogService
  ) {}

  ngOnInit(): void {
    this.isLoggedIn$ = this.authService.isUserLoggedIn()
    this.customCartService.setUomCode(null)
    if (this.product) {
      this.productCode = this.product.code
      this.setStockInfo(this.product)
      this.cd.markForCheck()
      this.price = this.product?.price?.formattedValue
    } else if (this.productCode) {
      // force hasStock and quantity for the time being, as we do not have more info:
      this.quantity = 1
      this.hasStock = true
      this.cd.markForCheck()
    } else {
      this.subscription = this.productDetailsService.getProductDetail$().subscribe((product: ICustomProduct) => {
        this.product = product
        this.productCode = product.code
        this.setStockInfo(product)
        this.resetProductInfo()
        this.cd.markForCheck()
        if (product.unit) {
          this.setDefaultUomSelectOption(product.unit)
          this.uomSelected.message = null
          this.uomSelected.code = product.unit.code
          this.uomSelected.title = product.unit.name
          this.defaultUomName = product.unit.name
          this.customCartService.setUomCode(product.unit.code)
        }
        if (product.uomMappings) {
          this.setUomSelectOptions(product.uomMappings)
        }
        this.price = product?.price?.formattedValue
      })
    }
  }

  public setDefaultUomSelectOption(unit: ProductUnit): void {
    const uomDefaultSelectOption: UomSelectOption = new UomSelectOption()
    uomDefaultSelectOption.default = true
    uomDefaultSelectOption.message = null
    uomDefaultSelectOption.title = unit.name
    uomDefaultSelectOption.code = unit.code
    this.uomSelectOptions.push(uomDefaultSelectOption)
  }

  public setUomSelectOptions(uomMappings: UomMapping[]): void {
    uomMappings.forEach((uomMapping) => {
      const uomSelectOption: UomSelectOption = new UomSelectOption()
      uomSelectOption.title = uomMapping.fromUnitName
      uomSelectOption.message = this.generateUomSelectOptionMessage(uomMapping)
      uomSelectOption.code = uomMapping.fromUnit
      uomSelectOption.default = false
      this.uomSelectOptions.push(uomSelectOption)
    })
  }

  public generateUomSelectOptionMessage(uomMapping: UomMapping): string {
    let message = ''
    if (uomMapping) {
      if (uomMapping.toUnitPluralName && uomMapping.toUnitPluralName.length > 0 && uomMapping.qtyConversion >= 1) {
        message =
          '1 ' +
          uomMapping.fromUnitName +
          ' contains ' +
          uomMapping.qtyConversion.toString() +
          ' ' +
          uomMapping.toUnitPluralName
      } else if (uomMapping.toUnitName && uomMapping.toUnitName.length > 0) {
        message =
          '1 ' +
          uomMapping.fromUnitName +
          ' contains ' +
          uomMapping.qtyConversion.toString() +
          ' ' +
          uomMapping.toUnitName
      }
    } else {
      message = null
    }
    return message
  }

  private setStockInfo(product: Product): void {
    this.quantity = 1
    this.hasStock = product.stock && product.stock.stockLevelStatus !== 'outOfStock'
    this.maxQuantity = this.productDetailsService.maxQuantity
  }

  updateCount(value: number): void {
    this.quantity = value
  }

  addToCart(): void {
    const quantity = this.addToCartForm.get('quantity').value
    if (!this.productCode || quantity <= 0) {
      return
    }
    this.activeCartService
      .getEntries()
      .pipe(take(1))
      .subscribe((entries) => {
        this.numberOfEntriesBeforeAdd = entries.length
        this.openModal()

        this.activeCartService.addEntry(this.productCode, quantity)
      })
  }

  // modalRef: any;
  @ViewChild('open') openElement: ElementRef
  protected openModal(): void {
    let modalInstance: any

    const dialogComponent = {
      productCode: this.productCode,
      quantity: this.quantity,
      entry$: this.activeCartService.getLastEntry(this.productCode),
      cart$: this.activeCartService.getActive(),
      loaded$: this.activeCartService.isStable(),
      uomCode: this.uomSelected.code,
      uomName: this.uomSelected.title,
      hasStock: this.hasStock,
      maxQuantity: this.maxQuantity,
      numberOfEntriesBeforeAdd: this.numberOfEntriesBeforeAdd
    }

    // this.dataService.recieve(dialogComponent);

    const dialog = this.launchDialogService.openDialog(
      // LAUNCH_CALLER.ADDED_TO_CART,
      LAUNCH_CALLER.CUSTOM_ADDED_TO_CART,
      this.openElement,
      this.vcr,
      //undefined,
      // CustomAddedToCartDialogComponent
      dialogComponent
    )

    //dialog?.pipe(take(1)).subscribe();
    // dialog?.pipe().subscribe();
    this.sharedService.dialogData(dialogComponent)

    if (dialog) {
      dialog?.pipe(take(1)).subscribe()
    }
  }

  ngOnDestroy(): void {
    this.customCartService.setUomCode(null)
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  public resetProductInfo(): void {
    this.customCartService.setUomCode(null)
    this.uomSelected = new UomSelectOption()
    this.uomSelectOptions = []
  }

  public onUomSelected(uomSelectOption: UomSelectOption): void {
    this.uomSelected = uomSelectOption

    this.customCartService.setUomCode(uomSelectOption.code)
    if (uomSelectOption.default) {
      this.uomSelected.message = null
    } else {
      this.uomSelected.message = uomSelectOption.message
    }
  }

  public get getStockColor(): string {
    switch (true) {
      case this.product?.stock?.stockLevelStatus === 'inStock':
        return 'in-stock'

      case this.product?.stock?.stockLevelStatus === 'lowStock':
        return 'low-stock'

      default:
        return 'out-of-stock'
    }
  }

  public get getStockIcon(): string {
    switch (true) {
      case this.product?.stock?.stockLevelStatus === 'inStock':
        return 'fa-check-circle'

      default:
        return 'fa-exclamation-circle'
    }
  }

  public discontinuedProduct(product: ICustomProduct): boolean {
    return this.customProductReplacementService.isDiscontinuedRegion(product)
  }
}
